import { useEffect, useRef, useState, useCallback } from "react";
import imageCompression from "browser-image-compression";
import {
    fetchHeroImages,
    uploadHeroImage,
    replaceHeroImage,
    updateHeroImage,
    toggleHeroImageActive,
    deleteHeroImage,
    searchLinkables, // add this export in service/api/orders.js — see note below the component
} from "../service/api/orders"; // adjust path to wherever you added the functions
import "../css/HeroImages.css";
import { ImageIcon, CheckIcon, ClockIcon, CloseIcon, EditIcon, UploadIcon, TrashIcon } from "../utils/icons";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const ACCEPTED_EXT_LABEL = "PNG, JPG, WEBP or GIF";
const COLOR_PRESETS = ["#ffffff", "#0f172a", "#6366f1", "#f43f5e", "#10b981", "#f59e0b"];

// Minimal inline styling for the new link picker UI so it works without
// touching HeroImages.css. Feel free to move these into the stylesheet
// under an "hi-link-*" namespace later — the classNames below already match.
const linkStyles = {
    row: { display: "flex", gap: 8, marginBottom: 8 },
    select: {
        flex: 1,
        padding: "10px 12px",
        borderRadius: 8,
        border: "1px solid #e2e8f0",
        fontSize: 14,
        background: "#fff",
    },
    searchInput: {
        width: "100%",
        padding: "9px 12px",
        borderRadius: 8,
        border: "1px solid #e2e8f0",
        fontSize: 14,
        marginBottom: 8,
        boxSizing: "border-box",
    },
    list: {
        maxHeight: 220,
        overflowY: "auto",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        background: "#fff",
    },
    listItem: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        cursor: "pointer",
        borderBottom: "1px solid #f1f5f9",
    },
    listItemImg: {
        width: 32,
        height: 32,
        borderRadius: 6,
        objectFit: "cover",
        background: "#f1f5f9",
        flexShrink: 0,
    },
    listItemName: { fontSize: 13.5, fontWeight: 500, color: "#0f172a" },
    listItemSub: { fontSize: 12, color: "#64748b" },
    pagerRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 6,
        fontSize: 12,
        color: "#64748b",
    },
    pagerBtn: {
        border: "1px solid #e2e8f0",
        background: "#fff",
        borderRadius: 6,
        padding: "4px 10px",
        fontSize: 12,
        cursor: "pointer",
    },
    pagerBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
    selectedBox: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        border: "1px solid #c7d2fe",
        background: "#eef2ff",
        borderRadius: 8,
    },
    selectedImg: {
        width: 36,
        height: 36,
        borderRadius: 8,
        objectFit: "cover",
        background: "#e2e8f0",
        flexShrink: 0,
    },
    changeBtn: {
        marginLeft: "auto",
        border: "none",
        background: "transparent",
        color: "#6366f1",
        fontSize: 12.5,
        fontWeight: 600,
        cursor: "pointer",
        whiteSpace: "nowrap",
    },
    emptyState: { padding: 16, textAlign: "center", fontSize: 13, color: "#94a3b8" },
    loadingState: { padding: 16, textAlign: "center", fontSize: 13, color: "#94a3b8" },
    errorText: { fontSize: 12.5, color: "#ef4444", marginTop: 4 },
};

export default function HeroImages() {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    useEffect(() => {
        loadImages();
    }, []);

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 3000);
        return () => clearTimeout(t);
    }, [toast]);

    async function loadImages() {
        setLoading(true);
        try {
            const res = await fetchHeroImages();
            setImages(res.data ?? []);
        } catch (e) {
            setToast({ type: "error", message: "Couldn't load hero images." });
        } finally {
            setLoading(false);
        }
    }

    // Independent toggle — flips just this image's active state, doesn't touch the others.
    async function handleToggleActive(id, currentlyActive) {
        setImages((imgs) =>
            imgs.map((img) => (img.id === id ? { ...img, is_active: !currentlyActive } : img))
        );
        try {
            const res = await toggleHeroImageActive(id);
            setImages((imgs) => imgs.map((img) => (img.id === id ? res.data : img)));
            setToast({
                type: "success",
                message: !currentlyActive ? "Hero image is now live." : "Hero image set to inactive.",
            });
        } catch (e) {
            // revert on failure
            setImages((imgs) =>
                imgs.map((img) => (img.id === id ? { ...img, is_active: currentlyActive } : img))
            );
            setToast({ type: "error", message: "Couldn't update that image's status." });
        }
    }

    async function handleDeleteConfirmed() {
        if (!deleteTarget) return;
        try {
            await deleteHeroImage(deleteTarget.id);
            setImages((imgs) => imgs.filter((img) => img.id !== deleteTarget.id));
            setToast({ type: "success", message: "Hero image deleted." });
        } catch (e) {
            setToast({ type: "error", message: "Couldn't delete that image." });
        } finally {
            setDeleteTarget(null);
        }
    }

    function handleSaved(saved, isNew) {
        setImages((imgs) =>
            isNew ? [saved, ...imgs] : imgs.map((img) => (img.id === saved.id ? saved : img))
        );
        setShowModal(false);
        setEditTarget(null);
        setToast({ type: "success", message: isNew ? "Hero image added." : "Hero image updated." });
    }

    const activeCount = images.filter((i) => i.is_active).length;

    return (
        <div className="hi-page">
            <div className="hi-header">
                <div>
                    <h1 className="hi-header__title">Hero Images</h1>
                    <div className="hi-header__sub">
                        Manage the homepage banner — upload images and switch each one live or inactive.
                    </div>
                </div>
                <button
                    className="hi-add-btn"
                    onClick={() => {
                        setEditTarget(null);
                        setShowModal(true);
                    }}
                >
                    <span className="hi-add-btn__icon">+</span>
                    Add Hero Image
                </button>
            </div>

            <div className="hi-stats">
                <div className="hi-stat">
                    <div className="hi-stat__icon hi-stat__icon--indigo"><ImageIcon /></div>
                    <div>
                        <div className="hi-stat__value">{images.length}</div>
                        <div className="hi-stat__label">Total images</div>
                    </div>
                </div>
                <div className="hi-stat">
                    <div className="hi-stat__icon hi-stat__icon--green"><CheckIcon /></div>
                    <div>
                        <div className="hi-stat__value">{activeCount}</div>
                        <div className="hi-stat__label">Currently live</div>
                    </div>
                </div>
                <div className="hi-stat">
                    <div className="hi-stat__icon hi-stat__icon--amber"><ClockIcon /></div>
                    <div>
                        <div className="hi-stat__value">{Math.max(images.length - activeCount, 0)}</div>
                        <div className="hi-stat__label">In Active</div>
                    </div>
                </div>
            </div>

            <div className="hi-toolbar">
                <span className="hi-count-label">
                    {loading ? "Loading…" : `${images.length} image${images.length === 1 ? "" : "s"}`}
                </span>
            </div>

            <div className="hi-grid">
                {loading &&
                    [0, 1, 2].map((i) => (
                        <div key={i} className="hi-skel-card">
                            <div className="hi-skel-media" />
                            <div className="hi-skel-line" />
                            <div className="hi-skel-line hi-skel-line--short" />
                        </div>
                    ))}

                {!loading && images.length === 0 && (
                    <div className="hi-empty">
                        <div className="hi-empty__icon"><ImageIcon size={32} /></div>
                        <div className="hi-empty__title">No hero images yet</div>
                        <div className="hi-empty__sub">Add one to get the homepage banner live.</div>
                    </div>
                )}

                {!loading &&
                    images.map((img) => (
                        <div key={img.id} className={`hi-card ${img.is_active ? "hi-card--active" : ""}`}>
                            <div className="hi-card__media">
                                <img className="hi-card__img" src={`https://food.totalfresh.co${img.image_path}`} alt={img.title || "Hero image"} />
                                <div
                                    className="hi-card__overlay-tint"
                                    style={{ background: img.overlay_color }}
                                />
                                <span
                                    className={`hi-card__ribbon ${
                                        img.is_active ? "hi-card__ribbon--active" : "hi-card__ribbon--inactive"
                                    }`}
                                >
                                    <span className={`hi-card__dot ${img.is_active ? "hi-card__dot--live" : ""}`} />
                                    {img.is_active ? "Live" : "Inactive"}
                                </span>
                                <button
                                    className="hi-card__del"
                                    onClick={() => setDeleteTarget(img)}
                                    title="Delete"
                                >
                                    <CloseIcon size={13} />
                                </button>
                            </div>

                            <div className="hi-card__body">
                                <div className={`hi-card__title ${!img.title ? "hi-card__title--muted" : ""}`}>
                                    {img.title || "Untitled"}
                                </div>
                                {img.metadata?.description && (
                                    <div className="hi-card__desc">{img.metadata.description}</div>
                                )}
                                {img.metadata?.link && (
                                    <div className="hi-card__desc">
                                        🔗 {linkSummary(img.metadata)}
                                    </div>
                                )}
                                <div className="hi-card__meta">
                                    {img.metadata?.width
                                        ? `${img.metadata.width}×${img.metadata.height}px · ${formatBytes(img.metadata.size)}`
                                        : "—"}
                                </div>

                                <div className="hi-card__footer">
                                    <div className="hi-swatch-group">
                                        <span className="hi-swatch" style={{ background: img.overlay_color }} />
                                        <span className="hi-swatch-code">{img.overlay_color}</span>
                                    </div>
                                    <div className="hi-card__actions">
                                        <button
                                            className="hi-icon-btn"
                                            title="Edit"
                                            onClick={() => {
                                                setEditTarget(img);
                                                setShowModal(true);
                                            }}
                                        >
                                            <EditIcon size={14} />
                                        </button>
                                    </div>
                                </div>

                                <label className="hi-toggle-row">
                                    <span className="hi-toggle-row__label">
                                        {img.is_active ? "Live" : "Inactive"}
                                    </span>
                                    <span
                                        className={`hi-toggle ${img.is_active ? "hi-toggle--on" : ""}`}
                                        role="switch"
                                        aria-checked={img.is_active}
                                        tabIndex={0}
                                        onClick={() => handleToggleActive(img.id, img.is_active)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                handleToggleActive(img.id, img.is_active);
                                            }
                                        }}
                                    >
                                        <span className="hi-toggle__knob" />
                                    </span>
                                </label>
                            </div>
                        </div>
                    ))}
            </div>

            {showModal && (
                <HeroImageModal
                    target={editTarget}
                    onClose={() => {
                        setShowModal(false);
                        setEditTarget(null);
                    }}
                    onSaved={handleSaved}
                    onError={(msg) => setToast({ type: "error", message: msg })}
                />
            )}

            {deleteTarget && (
                <div className="hi-del-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="hi-del-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="hi-del-modal__icon"><TrashIcon size={32} /></div>
                        <div className="hi-del-modal__title">Delete this hero image?</div>
                        <div className="hi-del-modal__desc">
                            {deleteTarget.title ? (
                                <>
                                    <span className="hi-del-modal__name">{deleteTarget.title}</span> will be removed
                                    permanently.
                                </>
                            ) : (
                                "This image will be removed permanently."
                            )}
                        </div>
                        <div className="hi-del-modal__actions">
                            <button className="hi-cancel-btn" onClick={() => setDeleteTarget(null)}>
                                Cancel
                            </button>
                            <button className="hi-confirm-del-btn" onClick={handleDeleteConfirmed}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toast && <div className={`hi-toast hi-toast--${toast.type}`}>{toast.message}</div>}
        </div>
    );
}

function formatBytes(bytes) {
    if (!bytes) return "";
    const kb = bytes / 1024;
    return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

// Human-readable one-liner for the card footer, based on how the backend
// stores link fields inside metadata (link_type + link_* fields + resolved `link`).
function linkSummary(metadata) {
    switch (metadata.link_type) {
        case "store":
            return metadata.link_store_name ? `Store: ${metadata.link_store_name}` : metadata.link;
        case "item":
            return metadata.link_item_name
                ? `Item: ${metadata.link_item_name}${metadata.link_store_name ? ` (${metadata.link_store_name})` : ""}`
                : metadata.link;
        case "custom":
            return metadata.link;
        default:
            return metadata.link;
    }
}

/* ─────────────────────────────────────────────
   Add / Edit modal
   ───────────────────────────────────────────── */
function HeroImageModal({ target, onClose, onSaved, onError }) {
    const isEdit = !!target;
    const fileInputRef = useRef(null);
    const meta = target?.metadata || {};

    const [title, setTitle] = useState(target?.title || "");
    const [description, setDescription] = useState(meta.description || "");
    const [color, setColor] = useState(target?.overlay_color || "#ffffff");
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(target?.image_path || null);
    const [compressing, setCompressing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [fileError, setFileError] = useState("");

    // ── Link Type state ──────────────────────────────────────────────
    // linkType: "" | "store" | "item" | "custom"
    const [linkType, setLinkType] = useState(meta.link_type || "");
    const [linkTouched, setLinkTouched] = useState(false); // did the user actually change the link section on an edit?

    const [selectedStore, setSelectedStore] = useState(
        meta.link_type === "store"
            ? { id: meta.link_store_id, name: meta.link_store_name, slug: meta.link_store_slug }
            : null
    );
    const [selectedItem, setSelectedItem] = useState(
        meta.link_type === "item"
            ? {
                id: meta.link_item_id,
                name: meta.link_item_name,
                restaurant_name: meta.link_store_name,
                restaurant_slug: meta.link_store_slug,
            }
            : null
    );

    const [customType, setCustomType] = useState(meta.link_custom_type || "url");
    const [customValue, setCustomValue] = useState(meta.link_custom_value || "");
    const [customError, setCustomError] = useState("");

    useEffect(() => {
        return () => {
            if (previewUrl && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    async function handleFileChange(e) {
        const selected = e.target.files?.[0];
        if (!selected) return;
        setFileError("");

        if (!ACCEPTED_TYPES.includes(selected.type)) {
            setFileError(`Please choose a ${ACCEPTED_EXT_LABEL} file.`);
            return;
        }

        if (selected.type === "image/gif") {
            // Skip compression — the library flattens animated GIFs to one frame.
            setFile(selected);
            setPreviewUrl(URL.createObjectURL(selected));
            return;
        }

        setCompressing(true);
        try {
            const compressed = await imageCompression(selected, {
                maxSizeMB: 1,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                fileType: selected.type,
                initialQuality: 0.8,
            });

            const compressedFile = new File([compressed], selected.name, { type: compressed.type });
            setFile(compressedFile);
            setPreviewUrl(URL.createObjectURL(compressedFile));
        } catch (err) {
            setFileError("Couldn't process that image — try a different file.");
        } finally {
            setCompressing(false);
        }
    }

    function handleLinkTypeChange(next) {
        setLinkType(next);
        setLinkTouched(true);
        setCustomError("");
        // clear the other branch's selection when switching type
        if (next !== "store") setSelectedStore(null);
        if (next !== "item") setSelectedItem(null);
    }

    function validateCustomLink() {
        if (linkType !== "custom") return true;
        if (!customValue.trim()) {
            setCustomError("This field is required.");
            return false;
        }
        if (customType === "url") {
            try {
                new URL(customValue);
            } catch {
                setCustomError("Enter a valid URL, including https://");
                return false;
            }
        } else if (customType === "tel") {
            if (!/^[+]?[0-9\-\s()]{7,20}$/.test(customValue)) {
                setCustomError("Enter a valid phone number.");
                return false;
            }
        } else if (customType === "email") {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customValue)) {
                setCustomError("Enter a valid email address.");
                return false;
            }
        }
        setCustomError("");
        return true;
    }

    function appendLinkFields(target) {
        // target is either a FormData instance or a plain object
        const set = (k, v) => {
            if (target instanceof FormData) target.append(k, v ?? "");
            else target[k] = v;
        };

        if (!linkTouched && isEdit) return; // link section untouched on edit — leave backend value as-is

        set("link_type", linkType || "");
        if (linkType === "store") {
            set("link_store_id", selectedStore?.id ?? "");
        } else if (linkType === "item") {
            set("link_item_id", selectedItem?.id ?? "");
        } else if (linkType === "custom") {
            set("link_custom_type", customType);
            set("link_custom_value", customValue);
        }
    }

    async function handleSubmit() {
        if (!isEdit && !file) {
            setFileError("Please choose an image to upload.");
            return;
        }
        if (linkType === "store" && !selectedStore) {
            onError("Please select a store for the link.");
            return;
        }
        if (linkType === "item" && !selectedItem) {
            onError("Please select an item for the link.");
            return;
        }
        if (!validateCustomLink()) return;

        setSaving(true);
        try {
            let res;

            if (!isEdit) {
                const formData = new FormData();
                formData.append("heroimage", file);
                formData.append("overlay_color", color);
                if (title) formData.append("title", title);
                if (description) formData.append("description", description);
                appendLinkFields(formData);
                res = await uploadHeroImage(formData);
            } else if (file) {
                const formData = new FormData();
                formData.append("heroimage", file);
                formData.append("overlay_color", color);
                if (title) formData.append("title", title);
                if (description) formData.append("description", description);
                appendLinkFields(formData);
                formData.append("_method", "PUT");
                res = await replaceHeroImage(target.id, formData);
            } else {
                const payload = { overlay_color: color, title, description };
                appendLinkFields(payload);
                res = await updateHeroImage(target.id, payload);
            }

            onSaved(res.data, !isEdit);
        } catch (err) {
            onError(err?.response?.data?.message || "Couldn't save the hero image.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="hi-overlay" onClick={onClose}>
            <div className="hi-modal" onClick={(e) => e.stopPropagation()}>
                <div className="hi-modal__header">
                    <div className="hi-modal__icon"><ImageIcon size={18} style={{ color: "#fff" }} /></div>
                    <div>
                        <div className="hi-modal__title">{isEdit ? "Edit Hero Image" : "Add Hero Image"}</div>
                        <div className="hi-modal__sub">
                            {isEdit ? "Update color, text, or replace the image" : "Upload an image for the homepage banner"}
                        </div>
                    </div>
                    <button className="hi-modal__close" onClick={onClose}><CloseIcon size={15} /></button>
                </div>

                <div className="hi-modal__body">
                    <div className="hi-form-grid">
                        <div className="hi-form-grid--span2">
                            <label className="hi-field__label">
                                Image <span className="hi-field__required">*</span>
                            </label>

                            <div
                                className={`hi-dropzone ${previewUrl ? "hi-dropzone--has-image" : "hi-dropzone--empty"}`}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {previewUrl ? (
                                    <img className="hi-dropzone__img" src={previewUrl} alt="Preview"/>
                                ) : (
                                    <>
                                        <div className="hi-dropzone__icon"><UploadIcon size={28}/></div>
                                        <div className="hi-dropzone__title">Click to choose an image</div>
                                        <div className="hi-dropzone__sub">
                                            {ACCEPTED_EXT_LABEL} · compressed automatically before upload
                                        </div>
                                    </>
                                )}
                            </div>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPTED_TYPES.join(",")}
                                onChange={handleFileChange}
                                style={{display: "none"}}
                            />

                            {isEdit && previewUrl && (
                                <div className="hi-dropzone__replace-hint">Click the preview to replace the image.</div>
                            )}

                            {compressing && (
                                <div className="hi-compressing">
                                    <span className="hi-spinner" style={{borderTopColor: "#6366f1"}}/>
                                    Compressing image…
                                </div>
                            )}

                            {fileError && <div className="hi-field__error">{fileError}</div>}
                        </div>

                        <div>
                            <label className="hi-field__label">Title (optional)</label>
                            <input
                                className="hi-input"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g. Summer Sale banner"
                            />
                        </div>

                        <div className="hi-form-grid--span2">
                            <label className="hi-field__label">Description text (optional)</label>
                            <textarea
                                className="hi-input hi-textarea"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="e.g. Flat 20% off on all fresh produce this weekend"
                                rows={3}
                            />
                        </div>

                        <div className="hi-form-grid--span2">
                            <label className="hi-field__label">Link (optional)</label>
                            <LinkTypeSelector
                                linkType={linkType}
                                onLinkTypeChange={handleLinkTypeChange}
                                selectedStore={selectedStore}
                                onSelectStore={(s) => {
                                    setSelectedStore(s);
                                    setLinkTouched(true);
                                }}
                                selectedItem={selectedItem}
                                onSelectItem={(i) => {
                                    setSelectedItem(i);
                                    setLinkTouched(true);
                                }}
                                customType={customType}
                                onCustomTypeChange={(t) => {
                                    setCustomType(t);
                                    setLinkTouched(true);
                                    setCustomError("");
                                }}
                                customValue={customValue}
                                onCustomValueChange={(v) => {
                                    setCustomValue(v);
                                    setLinkTouched(true);
                                    setCustomError("");
                                }}
                                customError={customError}
                            />
                        </div>

                        <div>
                            <label className="hi-field__label">Overlay Color</label>
                            <div className="hi-color-row">
                                <input
                                    type="color"
                                    className="hi-color-swatch-input"
                                    value={color}
                                    onChange={(e) => setColor(e.target.value)}
                                />
                                <input
                                    className="hi-input hi-input--mono"
                                    value={color}
                                    onChange={(e) => setColor(e.target.value)}
                                    placeholder="#ffffff"
                                />
                            </div>
                            <div className="hi-color-presets">
                                {COLOR_PRESETS.map((preset) => (
                                    <button
                                        key={preset}
                                        type="button"
                                        className={`hi-color-preset ${
                                            color.toLowerCase() === preset ? "hi-color-preset--selected" : ""
                                        }`}
                                        style={{background: preset}}
                                        onClick={() => setColor(preset)}
                                        title={preset}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="hi-modal__footer">
                    <button className="hi-cancel-btn" onClick={onClose}>Cancel</button>
                    <button className="hi-save-btn" onClick={handleSubmit} disabled={saving || compressing}>
                        {saving && <span className="hi-spinner" />}
                        {isEdit ? "Save Changes" : "Add Image"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────
   Link Type Selector — Store / Item / Custom
   ───────────────────────────────────────────── */
function LinkTypeSelector({
                              linkType,
                              onLinkTypeChange,
                              selectedStore,
                              onSelectStore,
                              selectedItem,
                              onSelectItem,
                              customType,
                              onCustomTypeChange,
                              customValue,
                              onCustomValueChange,
                              customError,
                          }) {
    return (
        <div>
            <div style={linkStyles.row}>
                <select
                    className="hi-input"
                    style={linkStyles.select}
                    value={linkType}
                    onChange={(e) => onLinkTypeChange(e.target.value)}
                >
                    <option value="">No link</option>
                    <option value="store">Store</option>
                    <option value="item">Item</option>
                    <option value="custom">Custom</option>
                </select>
            </div>

            {linkType === "store" &&
                (selectedStore ? (
                    <SelectedEntity
                        image={selectedStore.image}
                        title={selectedStore.name}
                        subtitle={selectedStore.slug ? `stores/${selectedStore.slug}` : ""}
                        onChange={() => onSelectStore(null)}
                    />
                ) : (
                    <LinkPicker type="store" onSelect={onSelectStore} />
                ))}

            {linkType === "item" &&
                (selectedItem ? (
                    <SelectedEntity
                        image={selectedItem.image}
                        title={selectedItem.name}
                        subtitle={selectedItem.restaurant_name ? `from ${selectedItem.restaurant_name}` : ""}
                        onChange={() => onSelectItem(null)}
                    />
                ) : (
                    <LinkPicker type="item" onSelect={onSelectItem} />
                ))}

            {linkType === "custom" && (
                <div>
                    <div style={linkStyles.row}>
                        <select
                            className="hi-input"
                            style={linkStyles.select}
                            value={customType}
                            onChange={(e) => onCustomTypeChange(e.target.value)}
                        >
                            <option value="url">Website URL</option>
                            <option value="tel">Phone Number</option>
                            <option value="email">Email Address</option>
                        </select>
                    </div>
                    <input
                        className="hi-input"
                        type={customType === "email" ? "email" : customType === "tel" ? "tel" : "url"}
                        value={customValue}
                        onChange={(e) => onCustomValueChange(e.target.value)}
                        placeholder={
                            customType === "url"
                                ? "https://food.totalfresh.co/offers/summer-sale"
                                : customType === "tel"
                                    ? "+91 98765 43210"
                                    : "offers@totalfresh.co"
                        }
                    />
                    {customError && <div style={linkStyles.errorText}>{customError}</div>}
                </div>
            )}
        </div>
    );
}

function SelectedEntity({ image, title, subtitle, onChange }) {
    return (
        <div style={linkStyles.selectedBox}>
            {image ? (
                <img src={image} alt={title} style={linkStyles.selectedImg} />
            ) : (
                <div style={linkStyles.selectedImg} />
            )}
            <div>
                <div style={linkStyles.listItemName}>{title || "Untitled"}</div>
                {subtitle && <div style={linkStyles.listItemSub}>{subtitle}</div>}
            </div>
            <button type="button" style={linkStyles.changeBtn} onClick={onChange}>
                Change
            </button>
        </div>
    );
}

// Server-side searchable picker shared by Store and Item, with infinite
// scroll inside the results container (loads the next page automatically
// as the user nears the bottom, instead of Prev/Next buttons).
// type: "store" | "item"
function LinkPicker({ type, onSelect }) {
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [results, setResults] = useState([]);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false); // initial / query-change load
    const [loadingMore, setLoadingMore] = useState(false); // subsequent page load
    const [error, setError] = useState("");

    const listRef = useRef(null);
    const requestIdRef = useRef(0); // guards against out-of-order responses when the query changes quickly

    // debounce the search input
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query), 350);
        return () => clearTimeout(t);
    }, [query]);

    // fetch a page and either replace (page 1 / new query) or append (infinite scroll)
    const fetchPage = useCallback(
        async (pageToLoad, { append }) => {
            const requestId = ++requestIdRef.current;
            append ? setLoadingMore(true) : setLoading(true);
            setError("");
            try {
                const res = await searchLinkables(type, debouncedQuery, pageToLoad);
                if (requestId !== requestIdRef.current) return; // superseded by a newer request

                const rows = res.data ?? [];
                const m = res.meta ?? { current_page: pageToLoad, last_page: pageToLoad, total: rows.length };

                setResults((prev) => (append ? [...prev, ...rows] : rows));
                setPage(m.current_page ?? pageToLoad);
                setLastPage(m.last_page ?? pageToLoad);
                setTotal(m.total ?? rows.length);
            } catch (e) {
                if (requestId !== requestIdRef.current) return;
                setError(`Couldn't load ${type === "store" ? "stores" : "items"}.`);
            } finally {
                if (requestId !== requestIdRef.current) return;
                append ? setLoadingMore(false) : setLoading(false);
            }
        },
        [type, debouncedQuery]
    );

    // reset + reload from page 1 whenever the search term (or type) changes
    useEffect(() => {
        setResults([]);
        setPage(1);
        setLastPage(1);
        if (listRef.current) listRef.current.scrollTop = 0;
        fetchPage(1, { append: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, debouncedQuery]);

    const hasMore = page < lastPage;

    function handleScroll(e) {
        if (loading || loadingMore || !hasMore) return;
        const el = e.target;
        const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
        if (nearBottom) {
            fetchPage(page + 1, { append: true });
        }
    }

    return (
        <div>
            <input
                style={linkStyles.searchInput}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={type === "store" ? "Search stores by name…" : "Search items by name…"}
            />

            <div style={linkStyles.list} ref={listRef} onScroll={handleScroll}>
                {loading && <div style={linkStyles.loadingState}>Loading…</div>}

                {!loading && error && <div style={linkStyles.errorText}>{error}</div>}

                {!loading && !error && results.length === 0 && (
                    <div style={linkStyles.emptyState}>
                        No {type === "store" ? "stores" : "items"} found{debouncedQuery ? ` for "${debouncedQuery}"` : ""}.
                    </div>
                )}

                {!loading &&
                    !error &&
                    results.map((r) => (
                        <div
                            key={r.id}
                            style={linkStyles.listItem}
                            onClick={() =>
                                onSelect(
                                    type === "store"
                                        ? { id: r.id, name: r.name, slug: r.slug, image: r.image }
                                        : {
                                            id: r.id,
                                            name: r.name,
                                            image: r.image,
                                            restaurant_name: r.restaurant_name,
                                            restaurant_slug: r.restaurant_slug,
                                        }
                                )
                            }
                        >
                            {r.image ? (
                                <img src={`https://food.totalfresh.co${r.image}`} alt={r.name} style={linkStyles.listItemImg} />
                            ) : (
                                <div style={linkStyles.listItemImg} />
                            )}
                            <div>
                                <div style={linkStyles.listItemName}>{r.name}</div>
                                {type === "item" && r.restaurant_name && (
                                    <div style={linkStyles.listItemSub}>{r.restaurant_name}</div>
                                )}
                            </div>
                        </div>
                    ))}

                {!loading && !error && loadingMore && (
                    <div style={linkStyles.loadingState}>Loading more…</div>
                )}
            </div>

            {!loading && !error && total > 0 && (
                <div style={{ ...linkStyles.pagerRow, justifyContent: "flex-end" }}>
                    <span>
                        {results.length} of {total}
                    </span>
                </div>
            )}
        </div>
    );
}